import { useState, useEffect, useRef, useMemo } from "react";
import {
  inventorySummaryApi,
  productApi,
  productCategoryApi,
  inventoryItemDtlApi,
  vendorApi,
  stockReceiptDtlApi,
  stockReceiptApi
} from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";
import InventoryHubNav from "../components/InventoryHubNav";
import BarcodeLabelPrinter from "./BarcodeLabelPrinter";

/* ══════════════════════════════════════
   TOAST
══════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ══════════════════════════════════════
   CONFIRM DIALOG
══════════════════════════════════════ */
function ConfirmDialog({ title = "Confirm Action", name, desc, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">{title}</h3>
        <p className="rv-dialog-desc">
          {desc ? desc : <><strong>{name}</strong> will be permanently removed. This action cannot be undone.</>}
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MANUAL ADJUSTMENT MODAL (STOCK BALANCES)
══════════════════════════════════════ */
function InventoryModal({ initial, products, inventory = [], onSave, onClose, saving }) {
  const isEdit = !!initial?.stockID;

  const [form, setForm] = useState({
    productID:      initial?.productID ?? "",
    adjustmentType: "add", // "add" | "remove"
    quantity:       "",
    reason:         "",
  });
  const [errors, setErrors] = useState({});

  const selectedProduct = useMemo(() => {
    if (!form.productID) return null;
    return (products || []).find(p => Number(p.productID) === Number(form.productID));
  }, [form.productID, products]);

  const existingStock = useMemo(() => {
    if (!form.productID) return null;
    return (inventory || []).find(i => Number(i.productID) === Number(form.productID));
  }, [form.productID, inventory]);

  const currentQty = existingStock ? Number(existingStock.quantity ?? 0) : 0;
  const adjQty = Number(form.quantity) || 0;
  const newQty = form.adjustmentType === "add" ? currentQty + adjQty : Math.max(0, currentQty - adjQty);

  const validate = () => {
    const e = {};
    if (!form.productID) e.productID = "Product is required.";
    if (form.quantity === "" || form.quantity === null) e.quantity = "Quantity is required.";
    else if (isNaN(Number(form.quantity)) || Number(form.quantity) <= 0) e.quantity = "Quantity must be greater than 0.";
    else if (form.adjustmentType === "remove" && Number(form.quantity) > currentQty) {
      e.quantity = `Cannot remove ${form.quantity} pcs. Current stock is only ${currentQty} pcs.`;
    }
    if (!form.reason || !form.reason.trim()) e.reason = "Reason for adjustment is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    const finalStockID = initial?.stockID || existingStock?.stockID;
    onSave({
      ...initial,
      stockID: finalStockID,
      productID: Number(form.productID),
      quantity: newQty,
    });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-inv-modal" style={{ maxWidth: "520px", width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><InvIcon /></div>
          <div>
            <h3 className="rv-modal-title">Manual Stock Adjustment</h3>
            <p className="rv-modal-sub">Update stock quantity for manual corrections and audits.</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", paddingBottom: "24px" }}>
          
          {/* Product Dropdown */}
          <div className="rv-field">
            <label className="rv-label">Product <span className="rv-required">*</span></label>
            <SearchableDropdown
              options={products}
              valueKey="productID"
              labelKey="productName"
              value={form.productID}
              onChange={val => {
                setForm(f => ({ ...f, productID: val }));
                setErrors(v => ({ ...v, productID: "" }));
              }}
              disabled={isEdit}
              placeholder="Select product…"
              searchPlaceholder="Search products…"
              error={errors.productID}
              pageSize={10}
            />
            {isEdit && <p className="rv-field-hint">Product cannot be changed during adjustment.</p>}
          </div>

          {/* Adjustment Type Segmented Control */}
          <div className="rv-field" style={{ marginTop: "4px" }}>
            <label className="rv-label">Adjustment Type <span className="rv-required">*</span></label>
            <div style={{ display: "flex", gap: "8px", background: "rgba(185, 167, 146, .12)", padding: "4px", borderRadius: "8px" }}>
              <button
                type="button"
                onClick={() => {
                  setForm(f => ({ ...f, adjustmentType: "add" }));
                  setErrors(v => ({ ...v, quantity: "" }));
                }}
                style={{
                  flex: 1,
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "12.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "all .18s",
                  background: form.adjustmentType === "add" ? "#fff" : "transparent",
                  color: form.adjustmentType === "add" ? "#2e7d32" : "var(--taupe, #8a7a6b)",
                  boxShadow: form.adjustmentType === "add" ? "0 2px 6px rgba(0,0,0,.08)" : "none"
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 700 }}>+</span> Add Stock
              </button>
              <button
                type="button"
                onClick={() => {
                  setForm(f => ({ ...f, adjustmentType: "remove" }));
                  setErrors(v => ({ ...v, quantity: "" }));
                }}
                style={{
                  flex: 1,
                  padding: "8px 14px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "12.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "all .18s",
                  background: form.adjustmentType === "remove" ? "#fff" : "transparent",
                  color: form.adjustmentType === "remove" ? "#c54030" : "var(--taupe, #8a7a6b)",
                  boxShadow: form.adjustmentType === "remove" ? "0 2px 6px rgba(0,0,0,.08)" : "none"
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 700 }}>−</span> Remove Stock
              </button>
            </div>
          </div>

          {/* Quantity Input */}
          <div className="rv-field" style={{ marginTop: "4px" }}>
            <label className="rv-label">Quantity to {form.adjustmentType === "add" ? "Add" : "Remove"} <span className="rv-required">*</span></label>
            <input
              type="number"
              min="1"
              className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`}
              placeholder="e.g. 10"
              value={form.quantity}
              onChange={e => {
                setForm(f => ({ ...f, quantity: e.target.value }));
                setErrors(v => ({ ...v, quantity: "" }));
              }}
            />
            {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
          </div>

          {/* Reason Input */}
          <div className="rv-field" style={{ marginTop: "4px" }}>
            <label className="rv-label">Reason <span className="rv-required">*</span></label>
            <input
              type="text"
              className={`rv-input ${errors.reason ? "rv-input--err" : ""}`}
              placeholder="e.g. Damaged item removed, Stock count correction"
              value={form.reason}
              onChange={e => {
                setForm(f => ({ ...f, reason: e.target.value }));
                setErrors(v => ({ ...v, reason: "" }));
              }}
            />
            {errors.reason && <p className="rv-field-err">{errors.reason}</p>}
          </div>

          {/* Product Live Info Preview */}
          {selectedProduct && (
            <div className="rv-inv-preview-card" style={{ marginTop: "10px" }}>
              <div className="rv-inv-preview-info">
                <span className="rv-inv-preview-name">
                  {selectedProduct.productName}
                </span>
                <span className="rv-inv-preview-sub">
                  Code: <strong>{selectedProduct.productCode || "—"}</strong>
                  {selectedProduct.purityStandard ? ` • ${selectedProduct.purityStandard}` : ""}
                  {selectedProduct.pricingType ? ` • ${selectedProduct.pricingType}` : ""}
                </span>
              </div>
              <div className="rv-inv-preview-badge">
                <span className="rv-inv-preview-stock">
                  Current Stock: {currentQty} pcs
                </span>
                {form.quantity !== "" && !isNaN(Number(form.quantity)) && Number(form.quantity) > 0 && (
                  <span style={{ fontSize: "11px", fontWeight: 700, color: form.adjustmentType === "add" ? "#2e7d32" : "#c54030" }}>
                    New Balance: {newQty} pcs ({form.adjustmentType === "add" ? `+${form.quantity}` : `-${form.quantity}`})
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Admin note */}
          <p style={{ margin: "14px 0 0 0", fontSize: "11.5px", color: "var(--taupe-dark, #8a7a6b)", lineHeight: "1.5" }}>
            ⚠️ This directly modifies stock balance. Use only for corrections, not for vendor deliveries.
          </p>
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            Apply Adjustment
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   INVENTORY ITEM FORM MODAL (TAGGED PIECES)
══════════════════════════════════════ */
function InventoryItemModal({ initial, onSave, onClose, saving, products, stocks, receiptLines = [], categories = [], vendors = [] }) {
  const isEdit = !!initial?.inventoryItemID;

  const initialProduct = useMemo(() => {
    if (!initial?.productID) return null;
    return products.find(p => p.productID === initial.productID) || null;
  }, [initial, products]);

  const [selectedCategory, setSelectedCategory] = useState(
    initialProduct?.categoryID ? String(initialProduct.categoryID) : ""
  );
  const [selectedVendor, setSelectedVendor] = useState(
    initialProduct?.vendorID ? String(initialProduct.vendorID) : ""
  );

  const [form, setForm] = useState({
    stockID:       initial?.stockID       ?? 0,
    productID:     initial?.productID     ?? 0,
    receiptLineID: initial?.receiptLineID ?? 0,
    grossWeight:   initial?.grossWeight   ?? "",
    lessWeight:    initial?.lessWeight    ?? "",
    netWeight:     initial?.netWeight     ?? "",
    quantity:      initial?.quantity      ?? "",
    barcodeID:     initial?.barcodeID     ?? 0,
  });

  const [referenceOpen, setReferenceOpen] = useState(
    !!(initial?.stockID || initial?.receiptLineID || initial?.barcodeID)
  );
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const g = parseFloat(form.grossWeight) || 0;
    const l = parseFloat(form.lessWeight)  || 0;
    if (form.grossWeight !== "" || form.lessWeight !== "") {
      setForm(f => ({ ...f, netWeight: parseFloat((g - l).toFixed(3)) }));
    }
  }, [form.grossWeight, form.lessWeight]);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (selectedCategory) {
      list = list.filter(p => String(p.categoryID ?? p.categoryId) === String(selectedCategory));
    }
    if (selectedVendor) {
      list = list.filter(p => String(p.vendorID ?? p.vendorId) === String(selectedVendor));
    }
    return list;
  }, [products, selectedCategory, selectedVendor]);

  const validate = () => {
    const e = {};
    if (!selectedCategory) e.category = "Category is required.";
    if (!form.productID || form.productID === 0) e.productID = "Product is required.";
    if (form.quantity === "" || isNaN(Number(form.quantity)) || Number(form.quantity) < 0)
      e.quantity = "Valid quantity is required.";
    if (form.grossWeight !== "" && isNaN(Number(form.grossWeight))) e.grossWeight = "Must be a valid number.";
    if (form.lessWeight  !== "" && isNaN(Number(form.lessWeight)))  e.lessWeight  = "Must be a valid number.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  const field = (key, value) => {
    setForm(f => ({ ...f, [key]: value }));
    setErrors(v => ({ ...v, [key]: "" }));
  };

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    setErrors(e => ({ ...e, category: "" }));
    if (catId && form.productID) {
      const p = products.find(item => item.productID === form.productID);
      if (p && String(p.categoryID ?? p.categoryId) !== String(catId)) {
        field("productID", 0);
      }
    }
  };

  const handleProductChange = (prodID) => {
    field("productID", prodID);
    const p = products.find(item => item.productID === prodID);
    if (p) {
      if (p.categoryID && !selectedCategory) {
        setSelectedCategory(String(p.categoryID));
      }
      if (p.vendorID && !selectedVendor) {
        setSelectedVendor(String(p.vendorID));
      }
    }
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-invitem-modal" style={{ maxWidth: "600px", width: "100%", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><InvItemIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Inventory Item" : "New Inventory Item"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update the item details below." : "Fill in details to add a new inventory item."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-modal-body" style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", paddingBottom: "120px" }}>
          
          {/* Step 1 */}
          <div className="rv-form-section" style={{ marginBottom: "18px" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--taupe-dark, #635343)", marginBottom: "12px" }}>
              Step 1 — What product is this item?
            </div>
            <div className="rv-field-row-2col">
              <div className="rv-field">
                <label className="rv-label">Category <span className="rv-required">*</span></label>
                <SearchableDropdown
                  options={categories.map(c => ({
                    id: String(c.categoryID ?? c.id),
                    name: c.categoryName ?? c.name
                  }))}
                  valueKey="id"
                  labelKey="name"
                  value={selectedCategory}
                  onChange={val => handleCategoryChange(val)}
                  placeholder="Select category…"
                  searchPlaceholder="Search categories…"
                  error={errors.category}
                  pageSize={10}
                />
              </div>
              <div className="rv-field">
                <label className="rv-label">Vendor / Supplier <span style={{ fontSize: "11px", fontWeight: 400, color: "var(--taupe)" }}>(Optional)</span></label>
                <SearchableDropdown
                  options={[
                    { id: "", name: "All / Any Vendor" },
                    ...vendors.map(v => ({
                      id: String(v.vendorID ?? v.id),
                      name: v.vendorName ?? v.name
                    }))
                  ]}
                  valueKey="id"
                  labelKey="name"
                  value={selectedVendor}
                  onChange={val => setSelectedVendor(val)}
                  placeholder="Select vendor / supplier…"
                  searchPlaceholder="Search vendors…"
                  pageSize={10}
                />
              </div>
            </div>
            <div className="rv-field" style={{ marginTop: "10px" }}>
              <label className="rv-label">Product <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={filteredProducts}
                valueKey="productID"
                labelKey="productName"
                value={form.productID}
                onChange={val => handleProductChange(Number(val))}
                placeholder="Select product…"
                searchPlaceholder="Search products…"
                error={errors.productID}
                pageSize={10}
              />
              <p className="rv-field-hint" style={{ marginTop: "4px" }}>Select a category first to narrow down products</p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="rv-form-section" style={{ marginBottom: "18px", paddingTop: "14px", borderTop: "1px solid rgba(185, 167, 146, .18)" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--taupe-dark, #635343)", marginBottom: "12px" }}>
              Step 2 — Physical details
            </div>
            <div className="rv-field-row-2col">
              <div className="rv-field">
                <label className="rv-label">Gross Weight (g)</label>
                <input
                  className={`rv-input ${errors.grossWeight ? "rv-input--err" : ""}`}
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="0.000"
                  value={form.grossWeight}
                  onChange={e => field("grossWeight", e.target.value)}
                />
                {errors.grossWeight && <p className="rv-field-err">{errors.grossWeight}</p>}
              </div>
              <div className="rv-field">
                <label className="rv-label">Less Weight (g)</label>
                <input
                  className={`rv-input ${errors.lessWeight ? "rv-input--err" : ""}`}
                  type="number"
                  step="0.001"
                  min="0"
                  placeholder="0.000"
                  value={form.lessWeight}
                  onChange={e => field("lessWeight", e.target.value)}
                />
                {errors.lessWeight && <p className="rv-field-err">{errors.lessWeight}</p>}
              </div>
            </div>
            <div className="rv-field-row-2col" style={{ marginTop: "10px" }}>
              <div className="rv-field">
                <label className="rv-label">Net Weight (g)</label>
                <input
                  className="rv-input rv-invitem-netweight"
                  type="number"
                  step="0.001"
                  placeholder="Auto-calculated"
                  value={form.netWeight}
                  readOnly
                  style={{ background: "rgba(185, 167, 146, .08)", cursor: "not-allowed" }}
                />
                <p className="rv-field-hint" style={{ marginTop: "4px" }}>Auto = Gross − Less</p>
              </div>
              <div className="rv-field">
                <label className="rv-label">Quantity <span className="rv-required">*</span></label>
                <input
                  className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`}
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.quantity}
                  onChange={e => field("quantity", e.target.value)}
                />
                {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div style={{ marginTop: "14px", border: "1px solid rgba(185, 167, 146, .22)", borderRadius: "8px", background: "#fff" }}>
            <button
              type="button"
              onClick={() => setReferenceOpen(!referenceOpen)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                background: "rgba(185, 167, 146, .08)",
                border: "none",
                borderTopLeftRadius: "7px",
                borderTopRightRadius: "7px",
                borderBottomLeftRadius: referenceOpen ? "0px" : "7px",
                borderBottomRightRadius: referenceOpen ? "0px" : "7px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "12px",
                color: "var(--dark, #2b2520)",
                fontFamily: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.04em"
              }}
            >
              <span>Step 3 — Reference (Optional)</span>
              <span style={{ fontSize: "11px", color: "var(--taupe, #8a7a6b)" }}>
                {referenceOpen ? "▲ Collapse" : "▼ Expand"}
              </span>
            </button>
            {referenceOpen && (
              <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: "12px", background: "#fff" }}>
                <div className="rv-field">
                  <label className="rv-label">Stock Receipt Reference</label>
                  <SearchableDropdown
                    options={[
                      { stockID: 0, label: "None / Auto-link" },
                      ...stocks.map(s => ({
                        stockID: s.stockID,
                        label: `Stock #${s.stockID} — ${products.find(p => p.productID === s.productID)?.productName ?? `Product ${s.productID}`} (Qty: ${s.quantity})`
                      }))
                    ]}
                    valueKey="stockID"
                    labelKey="label"
                    value={form.stockID}
                    onChange={val => field("stockID", Number(val))}
                    placeholder="None / Auto-link"
                    searchPlaceholder="Search stocks…"
                    pageSize={10}
                  />
                  <p className="rv-field-hint" style={{ marginTop: "4px" }}>Link this item to a vendor receipt if it came from one</p>
                </div>

                <div className="rv-field-row-2col">
                  <div className="rv-field">
                    <label className="rv-label">Receipt Line</label>
                    {receiptLines && receiptLines.length > 0 ? (
                      <SearchableDropdown
                        options={[
                          { receiptLineID: 0, label: "None / Direct Item" },
                          ...receiptLines.map(rl => {
                            const prod = products.find(p => p.productID === rl.productID);
                            const prefix = rl.receiptID ? `Receipt #${rl.receiptID} · ` : "";
                            return {
                              receiptLineID: rl.receiptLineID,
                              label: `${prefix}Line #${rl.receiptLineID} · ${prod?.productName ?? `Product #${rl.productID}`} (Qty: ${rl.quantity})`
                            };
                          })
                        ]}
                        valueKey="receiptLineID"
                        labelKey="label"
                        value={form.receiptLineID}
                        onChange={val => {
                          const id = Number(val);
                          field("receiptLineID", id);
                          if (id > 0) {
                            const line = receiptLines.find(l => l.receiptLineID === id);
                            if (line) {
                              if (line.productID && (!form.productID || form.productID === 0)) {
                                handleProductChange(Number(line.productID));
                              }
                              if (line.totalWeight && (!form.grossWeight || Number(form.grossWeight) === 0)) {
                                field("grossWeight", String(line.totalWeight));
                              }
                              if (line.quantity && (!form.quantity || Number(form.quantity) === 0)) {
                                field("quantity", String(line.quantity));
                              }
                            }
                          }
                        }}
                        placeholder="Link inward line…"
                        searchPlaceholder="Search line…"
                        pageSize={8}
                      />
                    ) : (
                      <input className="rv-input" type="number" min="0" placeholder="0" value={form.receiptLineID} onChange={e => field("receiptLineID", Number(e.target.value))} />
                    )}
                  </div>
                  <div className="rv-field">
                    <label className="rv-label">Barcode ID</label>
                    <input className="rv-input" type="number" min="0" placeholder="Auto (0)" value={form.barcodeID} onChange={e => field("barcodeID", Number(e.target.value))} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   INVENTORY ITEM DRAWER
══════════════════════════════════════ */
function InventoryItemDrawer({ item, products, stocks, onEdit, onPrint, onClose }) {
  const product = products.find(p => p.productID === item.productID);
  const stock   = stocks.find(s => s.stockID === item.stockID);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-invitem-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-invitem-drawer-header">
          <div className="rv-invitem-drawer-avatar"><InvItemIcon /></div>
          <div className="rv-invitem-drawer-title-wrap">
            <div className="rv-invitem-drawer-name">{product?.productName ?? `Product #${item.productID}`}</div>
            <span className="rv-invitem-id-chip">Item #{item.inventoryItemID}</span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>
        <div className="rv-invitem-drawer-body">
          <div className="rv-invitem-detail-grid">
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><BoxIcon /> Product</span>
              <span className="rv-vendor-detail-val">{product?.productName ?? "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> Stock Entry</span>
              <span className="rv-vendor-detail-val">{item.stockID ? `#${item.stockID}` : "—"}{stock ? ` (Qty: ${stock.quantity})` : ""}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Gross Weight</span>
              <span className="rv-vendor-detail-val">{item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Less Weight</span>
              <span className="rv-vendor-detail-val">{item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><WeightIcon /> Net Weight</span>
              <span className="rv-vendor-detail-val rv-invitem-netval">{item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><CubeIcon /> Quantity</span>
              <span className="rv-vendor-detail-val rv-invitem-qtyval">{item.quantity ?? "—"}</span>
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><HashIcon /> Receipt Line</span>
              {item.receiptLineID ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span className="rv-vendor-detail-val">Line #{item.receiptLineID}</span>
                  <button
                    type="button"
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--taupe-dark, #9e8872)",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      textDecoration: "underline",
                      padding: 0
                    }}
                    onClick={() => {
                      onClose();
                      window.dispatchEvent(new CustomEvent("reva:navigate", { detail: "Stock Receipt" }));
                    }}
                  >
                    View Inward Receipts →
                  </button>
                </div>
              ) : (
                <span className="rv-vendor-detail-val">—</span>
              )}
            </div>
            <div className="rv-invitem-detail-item">
              <span className="rv-vendor-detail-label"><BarcodeIcon /> Barcode ID</span>
              <span className="rv-vendor-detail-val">{item.barcodeID || "—"}</span>
            </div>
          </div>
        </div>
        <div className="rv-invitem-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--ghost" onClick={() => { onClose(); onPrint(item); }}>
            <PrintIcon /> Print Label
          </button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onClose(); onEdit(item); }}>
            <EditIcon /> Edit
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN STOCK PAGE (INVENTORY & STOCKS)
══════════════════════════════════════ */
export default function InventorySummaryPage() {
  /* ── Core data states ── */
  const [inventory,    setInventory]    = useState([]);
  const [products,     setProducts]     = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [items,        setItems]        = useState([]);
  const [vendors,      setVendors]      = useState([]);
  const [receiptLines, setReceiptLines] = useState([]);
  const [receipts,     setReceipts]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState(null);

  /* ── Top Section (Stock Balances) Filters & Modals ── */
  const [search,         setSearch]         = useState("");
  const [filterStock,    setFilterStock]    = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [modal,          setModal]          = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [confirm,        setConfirm]        = useState(null);

  /* ── Bottom Section (Tagged Items) Filters & Modals ── */
  const [taggedItemsOpen,    setTaggedItemsOpen]    = useState(false);
  const [itemSearch,         setItemSearch]         = useState("");
  const [filterItemCategory, setFilterItemCategory] = useState("all");
  const [filterItemVendor,   setFilterItemVendor]   = useState("all");
  const [itemModal,          setItemModal]          = useState(null);
  const [itemSaving,         setItemSaving]         = useState(false);
  const [itemConfirm,        setItemConfirm]        = useState(null);
  const [itemDrawer,         setItemDrawer]         = useState(null);
  const [printItem,          setPrintItem]          = useState(null);

  /* ── Table Resizing ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 220, 120, 140, 100]);

  const itemTableRef = useRef(null);
  const [itemTableReady, setItemTableReady] = useState(false);
  useResizableColumns(itemTableRef, itemTableReady, [50, 200, 110, 90, 90, 90, 100, 120]);

  /* ── Fetch All ── */
  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false); setItemTableReady(false);
      const [
        invData,
        prodData,
        catData,
        itemsData,
        vendorsData,
        receiptLinesData,
        receiptsData
      ] = await Promise.all([
        inventorySummaryApi.getAll(),
        productApi.getAll(),
        productCategoryApi.getAll().catch(() => []),
        inventoryItemDtlApi.getAll().catch(() => []),
        vendorApi.getAll().catch(() => []),
        stockReceiptDtlApi.getAll().catch(() => []),
        stockReceiptApi.getAll().catch(() => []),
      ]);

      setInventory(Array.isArray(invData) ? invData : invData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      setCategories(Array.isArray(catData) ? catData : catData?.data ?? []);
      setItems(Array.isArray(itemsData) ? itemsData : itemsData?.data ?? []);
      setVendors(Array.isArray(vendorsData) ? vendorsData : vendorsData?.data ?? []);

      const allReceipts = Array.isArray(receiptsData) ? receiptsData : receiptsData?.data ?? [];
      setReceipts(allReceipts);
      const allLines = Array.isArray(receiptLinesData) ? receiptLinesData : receiptLinesData?.data ?? [];

      if (allReceipts.length > 0) {
        const validReceiptIds = new Set(allReceipts.map(r => Number(r.receiptID)));
        setReceiptLines(allLines.filter(line => validReceiptIds.has(Number(line.receiptID))));
      } else {
        setReceiptLines(allLines);
      }
    } catch (err) {
      showToast(err.message || "Failed to load inventory.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);
  useEffect(() => { if (taggedItemsOpen && !loading) setItemTableReady(true); }, [taggedItemsOpen, loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Product name lookup ── */
  const productMap = Object.fromEntries(products.map(p => [p.productID, p.productName]));
  const productName = (id) => products.find(p => p.productID === id)?.productName ?? `Product #${id}`;

  /* ── Stats Strip ── */
  const totalSkus  = inventory.length;
  const totalQty   = inventory.reduce((sum, i) => sum + (i.quantity ?? 0), 0);
  const outOfStock = inventory.filter(i => (i.quantity ?? 0) === 0).length;
  const lowStock   = inventory.filter(i => (i.quantity ?? 0) > 0 && (i.quantity ?? 0) <= 5).length;

  /* ── Save Stock Adjustment ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.stockID) {
        await inventorySummaryApi.update(formData);
        showToast("Stock entry updated successfully.");
      } else {
        await inventorySummaryApi.create(formData);
        showToast("Stock entry added successfully.");
      }
      setModal(null);
      emitDataMutation("stock");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete Stock Entry ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await inventorySummaryApi.remove(confirm.id);
      showToast("Stock entry deleted.");
      setConfirm(null);
      emitDataMutation("stock");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Save Tagged Inventory Item ── */
  const handleSaveItem = async (formData) => {
    setItemSaving(true);
    try {
      if (formData.inventoryItemID) {
        await inventoryItemDtlApi.update(formData);
        showToast("Inventory item updated successfully.");
      } else {
        await inventoryItemDtlApi.create(formData);
        showToast("Inventory item created successfully.");
      }
      setItemModal(null);
      emitDataMutation("stock");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setItemSaving(false);
    }
  };

  /* ── Delete Tagged Inventory Item ── */
  const handleDeleteItem = async () => {
    if (!itemConfirm) return;
    try {
      await inventoryItemDtlApi.remove(itemConfirm.id);
      showToast("Inventory item deleted.");
      setItemConfirm(null);
      emitDataMutation("stock");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Stock level helper ── */
  const getStockLevel = (qty) => {
    if (qty === 0)   return { label: "Out of Stock", cls: "rv-stock-pill--out" };
    if (qty <= 5)    return { label: "Low Stock",    cls: "rv-stock-pill--low" };
    if (qty <= 50)   return { label: "In Stock",     cls: "rv-stock-pill--in" };
    return              { label: "Well Stocked",  cls: "rv-stock-pill--well" };
  };

  /* ── Top Section Filtering (Stock Balances) ── */
  const filteredStock = inventory.filter(item => {
    const prod = products.find(p => p.productID === item.productID);
    const pName = prod?.productName ?? productMap[item.productID] ?? "";
    const matchSearch =
      !search ||
      pName.toLowerCase().includes(search.toLowerCase()) ||
      String(item.quantity).includes(search);

    const qty = item.quantity ?? 0;
    const matchStock =
      filterStock === "all"     ? true :
      filterStock === "out"     ? qty === 0 :
      filterStock === "low"     ? qty > 0 && qty <= 5 :
      filterStock === "instock" ? qty > 5 : true;

    const itemCatId = String(prod?.categoryID ?? prod?.categoryId ?? "");
    const matchCategory = filterCategory === "all" || itemCatId === String(filterCategory);

    return matchSearch && matchStock && matchCategory;
  });

  const {
    paged: pagedStock,
    page: stockPage,
    totalPages: stockTotalPages,
    pageSize: stockPageSize,
    setPage: setStockPage,
    setPageSize: setStockPageSize,
    pageStart: stockPageStart,
    pageEnd: stockPageEnd,
    totalItems: stockTotalItems,
  } = usePagination(filteredStock, 10);

  /* ── Bottom Section Filtering (Tagged Items) ── */
  const filteredItems = items.filter(item => {
    const prod = products.find(p => p.productID === item.productID);
    const pName = (prod?.productName ?? productName(item.productID)).toLowerCase();
    const matchSearch =
      !itemSearch ||
      pName.includes(itemSearch.toLowerCase()) ||
      String(item.inventoryItemID).includes(itemSearch) ||
      String(item.barcodeID).includes(itemSearch);

    const itemCatId = String(prod?.categoryID ?? prod?.categoryId ?? "");
    const matchCategory = filterItemCategory === "all" || itemCatId === String(filterItemCategory);

    let itemVendorId = prod?.vendorID ?? prod?.vendorId;
    if (!itemVendorId && item.receiptLineID) {
      const line = receiptLines.find(l => l.receiptLineID === item.receiptLineID);
      if (line?.receiptID) {
        const rcpt = receipts.find(r => r.receiptID === line.receiptID);
        if (rcpt?.vendorID) itemVendorId = rcpt.vendorID;
      }
    }
    const matchVendor = filterItemVendor === "all" || String(itemVendorId ?? "") === String(filterItemVendor);

    return matchSearch && matchCategory && matchVendor;
  });

  const {
    paged: pagedItems,
    page: itemPage,
    totalPages: itemTotalPages,
    pageSize: itemPageSize,
    setPage: setItemPage,
    setPageSize: setItemPageSize,
    pageStart: itemPageStart,
    pageEnd: itemPageEnd,
    totalItems: itemTotalItems,
  } = usePagination(filteredItems, 10);

  return (
    <div className="rv-inv-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {confirm && (
        <ConfirmDialog
          title="Delete Stock Entry?"
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {itemConfirm && (
        <ConfirmDialog
          title="Delete Inventory Item?"
          name={itemConfirm.name}
          onConfirm={handleDeleteItem}
          onCancel={() => setItemConfirm(null)}
        />
      )}

      {modal && (
        <InventoryModal
          initial={modal.data}
          products={products}
          inventory={inventory}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}

      {itemModal && (
        <InventoryItemModal
          initial={itemModal.data}
          onSave={handleSaveItem}
          onClose={() => setItemModal(null)}
          saving={itemSaving}
          products={products}
          stocks={inventory}
          receiptLines={receiptLines}
          categories={categories}
          vendors={vendors}
        />
      )}

      {itemDrawer && (
        <InventoryItemDrawer
          item={itemDrawer}
          products={products}
          stocks={inventory}
          onEdit={(item) => setItemModal({ data: item })}
          onPrint={(item) => setPrintItem(item)}
          onClose={() => setItemDrawer(null)}
        />
      )}

      {printItem && (
        <BarcodeLabelPrinter
          item={printItem}
          product={products.find(p => p.productID === printItem.productID)}
          products={products}
          vendors={vendors}
          onClose={() => setPrintItem(null)}
        />
      )}

      <InventoryHubNav active="Stocks" />

      {/* ── Topbar (Stock Overview) ── */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 className="rv-inv-heading">Stock</h2>
            <span className="rv-inv-count">{inventory.length} entries</span>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "var(--taupe, #8a7a6b)" }}>
            Current stock levels per product. Use Vendor Receipts tab to record incoming deliveries from suppliers.
          </p>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> Manual Adjustment
        </button>
      </div>

      {/* ── Stats Strip ── */}
      <div className="rv-inv-stats-strip" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(185, 167, 146, .14)", color: "var(--taupe-dark, #635343)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <BoxIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total SKUs</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--dark, #2b2520)", fontFamily: "var(--d-font)" }}>{totalSkus}</div>
          </div>
        </div>

        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(90, 160, 100, .14)", color: "#3e7d46", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <LayersIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Quantity</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--dark, #2b2520)", fontFamily: "var(--d-font)" }}>{totalQty.toLocaleString()}</div>
          </div>
        </div>

        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(220, 80, 70, .12)", color: "#c54030", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AlertIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Out of Stock</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: outOfStock > 0 ? "#c54030" : "var(--dark, #2b2520)", fontFamily: "var(--d-font)" }}>{outOfStock}</div>
          </div>
        </div>

        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(214, 158, 60, .14)", color: "#b07c20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <WarnIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Low Stock (&le; 5)</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#b07c20", fontFamily: "var(--d-font)" }}>{lowStock}</div>
          </div>
        </div>
      </div>

      {/* ── Top Filters (Search, Category, Status tabs) ── */}
      <div className="rv-inv-filters" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
        <div className="rv-inv-search" style={{ minWidth: "220px", flex: 1 }}>
          <SearchIcon />
          <input
            placeholder="Search by product name or quantity…"
            value={search}
            onChange={e => { setSearch(e.target.value); setStockPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setStockPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div style={{ width: "210px", flexShrink: 0 }}>
          <SearchableDropdown
            options={[
              { id: "all", name: "All Categories" },
              ...categories.map(c => ({ id: String(c.categoryID ?? c.id), name: c.categoryName ?? c.name }))
            ]}
            valueKey="id"
            labelKey="name"
            value={filterCategory}
            onChange={val => { setFilterCategory(val); setStockPage(1); }}
            placeholder="All Categories"
            searchPlaceholder="Search categories…"
            pageSize={10}
          />
        </div>

        <div className="rv-filter-tabs">
          {[
            { key: "all",     label: "All" },
            { key: "instock", label: "In Stock" },
            { key: "low",     label: "Low Stock" },
            { key: "out",     label: "Out of Stock" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`rv-filter-tab ${filterStock === key ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStock(key); setStockPage(1); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stock Balance Table Card ── */}
      <div className="rv-card rv-inv-card">
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-inv-empty">
              <div className="rv-loader" />
              <p>Loading stock balances…</p>
            </div>
          ) : filteredStock.length === 0 ? (
            <div className="rv-inv-empty">
              <div className="rv-empty-icon"><InvIcon /></div>
              <p className="rv-empty-title">No stock entries found</p>
              <p className="rv-empty-sub">
                {search || filterCategory !== "all" || filterStock !== "all"
                  ? "Try adjusting your filters or search terms."
                  : "Add your first manual adjustment to get started."}
              </p>
              {!search && filterCategory === "all" && filterStock === "all" && (
                <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> Manual Adjustment
                </button>
              )}
            </div>
          ) : (
            <div className="rv-table-wrap rv-inv-table-wrap">
              <table className="rv-table rv-inv-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Quantity</th>
                    <th>Stock Level</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedStock.map((item, i) => {
                    const stock = getStockLevel(item.quantity ?? 0);
                    return (
                      <tr key={item.stockID} className="rv-inv-row">
                        <td className="rv-inv-idx">{stockPageStart + i}</td>
                        <td>
                          <div className="rv-inv-product-cell">
                            <span className="rv-inv-product-name">
                              {productMap[item.productID] ?? `Product #${item.productID}`}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="rv-inv-qty">{(item.quantity ?? 0).toLocaleString()}</span>
                        </td>
                        <td>
                          <span className={`rv-stock-pill ${stock.cls}`}>
                            <span className="rv-status-dot" />
                            {stock.label}
                          </span>
                        </td>
                        <td>
                          <div className="rv-actions">
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => setModal({ data: item })}
                            >
                              <EditIcon />
                            </button>
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => setConfirm({
                                id: item.stockID,
                                name: productMap[item.productID] ?? `Stock #${item.stockID}`,
                              })}
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={pagedStock}
            emptyIcon={<InvIcon />}
            emptyTitle="No stock entries found"
            emptySubtitle={search ? "Try a different search term." : "Add your first manual adjustment to get started."}
            emptyAction={
              !search && (
                <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                  <PlusIcon /> Manual Adjustment
                </button>
              )
            }
            renderCard={(item, i) => {
              const stock = getStockLevel(item.quantity ?? 0);
              return (
                <RvDataCard
                  key={item.stockID}
                  index={stockPageStart + i}
                  title={productMap[item.productID] ?? `Product #${item.productID}`}
                  subtitle={`Stock Entry #${item.stockID}`}
                  badge={
                    <span className={`rv-stock-pill ${stock.cls}`}>
                      <span className="rv-status-dot" />
                      {stock.label}
                    </span>
                  }
                  fields={[
                    { label: "Quantity", value: (item.quantity ?? 0).toLocaleString() },
                    { label: "Stock Level", value: stock.label },
                  ]}
                  onClick={() => setModal({ data: item })}
                  actions={
                    <div className="rv-actions" onClick={e => e.stopPropagation()}>
                      <button
                        className="rv-action-btn rv-action-btn--edit"
                        title="Edit"
                        onClick={() => setModal({ data: item })}
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="rv-action-btn rv-action-btn--del"
                        title="Delete"
                        onClick={() => setConfirm({
                          id: item.stockID,
                          name: productMap[item.productID] ?? `Stock #${item.stockID}`,
                        })}
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  }
                />
              );
            }}
          />
        </div>

        {!loading && filteredStock.length > 0 && (
          <Pagination
            page={stockPage}
            totalPages={stockTotalPages}
            pageSize={stockPageSize}
            setPage={setStockPage}
            setPageSize={setStockPageSize}
            pageStart={stockPageStart}
            pageEnd={stockPageEnd}
            totalItems={stockTotalItems}
          />
        )}
      </div>

      {/* ══════════════════════════════════════
         BOTTOM SECTION: INDIVIDUAL TAGGED ITEMS
      ══════════════════════════════════════ */}
      <div className="rv-tagged-items-section" style={{ marginTop: "24px" }}>
        
        {/* Collapsible Header Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            padding: "16px 20px",
            background: "linear-gradient(135deg, rgba(185, 167, 146, .12) 0%, rgba(185, 167, 146, .03) 100%)",
            border: "1.5px solid rgba(185, 167, 146, .22)",
            borderRadius: taggedItemsOpen ? "12px 12px 0 0" : "12px",
            cursor: "pointer",
            transition: "all .2s ease"
          }}
          onClick={() => setTaggedItemsOpen(v => !v)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(185, 167, 146, .18)", color: "var(--taupe-dark, #635343)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TagIcon />
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--dark, #2b2520)", fontFamily: "var(--d-font)" }}>
                Individual Tagged Items
              </div>
              <div style={{ fontSize: "11.5px", color: "var(--taupe, #8a7a6b)" }}>
                {items.length} tagged physical pieces with weight and barcodes
              </div>
            </div>
          </div>

          <button
            type="button"
            className="rv-btn rv-btn--ghost"
            style={{ fontSize: "12.5px", fontWeight: 600, pointerEvents: "none" }}
          >
            {taggedItemsOpen ? "▲ Hide Tagged Items" : "▼ Show Tagged Items (Jewelry Items & Barcodes)"}
          </button>
        </div>

        {/* Collapsible Content Body */}
        {taggedItemsOpen && (
          <div
            style={{
              border: "1.5px solid rgba(185, 167, 146, .22)",
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              padding: "20px",
              background: "#fff"
            }}
          >
            {/* Context Note */}
            <p style={{ margin: "0 0 16px 0", fontSize: "12.5px", color: "var(--taupe-dark, #635343)", background: "rgba(185, 167, 146, .08)", padding: "10px 14px", borderRadius: "8px", borderLeft: "3px solid var(--taupe, #b9a792)" }}>
              These are individually tagged physical pieces. Each row is one specific item with its own weight and barcode — not a product count.
            </p>

            {/* Cascade Category + Vendor Filters & New Item Action */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "14px" }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", flex: 1 }}>
                <div style={{ minWidth: "180px", flexShrink: 0 }}>
                  <SearchableDropdown
                    options={[
                      { id: "all", name: "All Categories" },
                      ...categories.map(c => ({ id: String(c.categoryID ?? c.id), name: c.categoryName ?? c.name }))
                    ]}
                    valueKey="id"
                    labelKey="name"
                    value={filterItemCategory}
                    onChange={val => { setFilterItemCategory(val); setItemPage(1); }}
                    placeholder="All Categories"
                    searchPlaceholder="Search categories…"
                    pageSize={10}
                  />
                </div>
                <div style={{ minWidth: "180px", flexShrink: 0 }}>
                  <SearchableDropdown
                    options={[
                      { id: "all", name: "All Vendors" },
                      ...vendors.map(v => ({ id: String(v.vendorID ?? v.id), name: v.vendorName ?? v.name }))
                    ]}
                    valueKey="id"
                    labelKey="name"
                    value={filterItemVendor}
                    onChange={val => { setFilterItemVendor(val); setItemPage(1); }}
                    placeholder="All Vendors"
                    searchPlaceholder="Search vendors…"
                    pageSize={10}
                  />
                </div>
                {(filterItemCategory !== "all" || filterItemVendor !== "all" || itemSearch) && (
                  <button
                    type="button"
                    className="rv-clear-filters-link"
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--taupe-dark, #635343)",
                      fontSize: "12.5px",
                      fontWeight: 600,
                      cursor: "pointer",
                      textDecoration: "underline",
                      padding: "4px 8px"
                    }}
                    onClick={() => {
                      setFilterItemCategory("all");
                      setFilterItemVendor("all");
                      setItemSearch("");
                      setItemPage(1);
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              <button
                type="button"
                className="rv-btn rv-btn--primary"
                onClick={() => setItemModal({ data: {} })}
              >
                <PlusIcon /> New Item
              </button>
            </div>

            {/* Tagged Items Search Bar */}
            <div className="rv-invitem-filters" style={{ marginBottom: "16px" }}>
              <div className="rv-invitem-search">
                <SearchIcon />
                <input
                  placeholder="Search by product, item ID, barcode…"
                  value={itemSearch}
                  onChange={e => { setItemSearch(e.target.value); setItemPage(1); }}
                />
                {itemSearch && (
                  <button className="rv-invitem-search-clear" onClick={() => { setItemSearch(""); setItemPage(1); }}>
                    <CloseIcon />
                  </button>
                )}
              </div>
            </div>

            {/* Tagged Items Desktop Table */}
            <div className="rv-desktop-table-view">
              {loading ? (
                <div className="rv-invitem-empty">
                  <div className="rv-loader" />
                  <p>Loading tagged items…</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="rv-invitem-empty">
                  <div className="rv-empty-icon"><InvItemIcon /></div>
                  <p className="rv-empty-title">No tagged items found</p>
                  <p className="rv-empty-sub">
                    {itemSearch || filterItemCategory !== "all" || filterItemVendor !== "all"
                      ? "Try adjusting your search or filter dropdowns."
                      : "Add your first tagged piece to get started."}
                  </p>
                  {!itemSearch && filterItemCategory === "all" && filterItemVendor === "all" && (
                    <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setItemModal({ data: {} })}>
                      <PlusIcon /> New Item
                    </button>
                  )}
                </div>
              ) : (
                <div className="rv-table-wrap rv-invitem-table-wrap">
                  <table className="rv-table rv-invitem-table" ref={itemTableRef}>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>Gross Wt</th>
                        <th>Less Wt</th>
                        <th>Net Wt</th>
                        <th>Qty</th>
                        <th>Barcode ID</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedItems.map((item, i) => (
                        <tr
                          key={item.inventoryItemID}
                          className="rv-invitem-row"
                          onClick={() => setItemDrawer(item)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="rv-invitem-idx">{itemPageStart + i}</td>
                          <td>
                            <div className="rv-invitem-product-cell">
                              <span className="rv-invitem-product-name">{productName(item.productID)}</span>
                              {item.stockID ? <span className="rv-invitem-stock-chip">{item.stockID}</span> : null}
                            </div>
                          </td>
                          <td>
                            <span className="rv-invitem-weight">
                              {item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : <span className="rv-empty-val">—</span>}
                            </span>
                          </td>
                          <td>
                            <span className="rv-invitem-weight">
                              {item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : <span className="rv-empty-val">—</span>}
                            </span>
                          </td>
                          <td>
                            <span className="rv-invitem-netweight-val">
                              {item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : <span className="rv-empty-val">—</span>}
                            </span>
                          </td>
                          <td><span className="rv-invitem-qty">{item.quantity ?? 0}</span></td>
                          <td>
                            {item.barcodeID
                              ? <span className="rv-invitem-barcode">{item.barcodeID}</span>
                              : <span className="rv-empty-val">—</span>}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="rv-actions">
                              <button
                                className="rv-action-btn rv-action-btn--print"
                                title="Print Label"
                                onClick={() => setPrintItem(item)}
                              >
                                <PrintIcon />
                              </button>
                              <button
                                className="rv-action-btn rv-action-btn--edit"
                                title="Edit"
                                onClick={() => setItemModal({ data: item })}
                              >
                                <EditIcon />
                              </button>
                              <button
                                className="rv-action-btn rv-action-btn--del"
                                title="Delete"
                                onClick={() => setItemConfirm({ id: item.inventoryItemID, name: `Item #${item.inventoryItemID}` })}
                              >
                                <TrashIcon />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Tagged Items Mobile View */}
            <div className="rv-mobile-cards-view">
              <RvDataCardList
                loading={loading}
                items={pagedItems}
                emptyIcon={<InvItemIcon />}
                emptyTitle="No tagged items found"
                emptySubtitle={itemSearch ? "Try a different search term." : "Add your first tagged item to get started."}
                emptyAction={
                  !itemSearch && (
                    <button className="rv-btn rv-btn--primary" onClick={() => setItemModal({ data: {} })}>
                      <PlusIcon /> New Item
                    </button>
                  )
                }
                renderCard={(item, i) => (
                  <RvDataCard
                    key={item.inventoryItemID}
                    index={itemPageStart + i}
                    title={productName(item.productID)}
                    subtitle={`Item #${item.inventoryItemID}${item.barcodeID ? ` • Barcode: ${item.barcodeID}` : ""}`}
                    badge={item.stockID ? <span className="rv-invitem-stock-chip">Stock #{item.stockID}</span> : null}
                    fields={[
                      { label: "Gross Wt", value: item.grossWeight != null && item.grossWeight !== 0 ? `${item.grossWeight} g` : "—" },
                      { label: "Less Wt",  value: item.lessWeight != null && item.lessWeight !== 0 ? `${item.lessWeight} g` : "—" },
                      { label: "Net Wt",   value: item.netWeight != null && item.netWeight !== 0 ? `${item.netWeight} g` : "—" },
                      { label: "Quantity", value: item.quantity ?? 0 },
                    ]}
                    onClick={() => setItemDrawer(item)}
                    actions={
                      <div className="rv-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="rv-action-btn rv-action-btn--print"
                          title="Print Label"
                          onClick={() => setPrintItem(item)}
                        >
                          <PrintIcon />
                        </button>
                        <button
                          className="rv-action-btn rv-action-btn--edit"
                          title="Edit"
                          onClick={() => setItemModal({ data: item })}
                        >
                          <EditIcon />
                        </button>
                        <button
                          className="rv-action-btn rv-action-btn--del"
                          title="Delete"
                          onClick={() => setItemConfirm({ id: item.inventoryItemID, name: `Item #${item.inventoryItemID}` })}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    }
                  />
                )}
              />
            </div>

            {!loading && filteredItems.length > 0 && (
              <div style={{ marginTop: "14px" }}>
                <Pagination
                  page={itemPage}
                  totalPages={itemTotalPages}
                  pageSize={itemPageSize}
                  setPage={setItemPage}
                  setPageSize={setItemPageSize}
                  pageStart={itemPageStart}
                  pageEnd={itemPageEnd}
                  totalItems={itemTotalItems}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── View Movement Ledger Link at the very bottom ── */}
      <div style={{ marginTop: "28px", paddingBottom: "20px", display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("reva:navigate", { detail: "Inventory Transaction" }))}
          style={{
            background: "none",
            border: "none",
            color: "var(--taupe-dark, #635343)",
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 12px",
            borderRadius: "6px",
            transition: "background .15s ease"
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(185, 167, 146, .14)"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}
        >
          View Movement Ledger →
        </button>
      </div>

    </div>
  );
}

/* ── Icons ── */
function PlusIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function EditIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>; }
function TrashIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function CloseIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function InvIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 8h14M5 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-9 4h4"/><path d="M9 3h6l2 5H7L9 3z"/></svg>; }
function BoxIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function LayersIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>; }
function WarnIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
function GripIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{width:13,height:13}}><circle cx="9" cy="5" r="1" fill="currentColor"/><circle cx="9" cy="12" r="1" fill="currentColor"/><circle cx="9" cy="19" r="1" fill="currentColor"/><circle cx="15" cy="5" r="1" fill="currentColor"/><circle cx="15" cy="12" r="1" fill="currentColor"/><circle cx="15" cy="19" r="1" fill="currentColor"/></svg>; }
function TagIcon()          { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="16" height="16"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function InvItemIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function WeightIcon()       { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="8" r="4"/><path d="M6 20h12l-2-8H8L6 20z"/></svg>; }
function CubeIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 3H8l-2 4h12l-2-4z"/></svg>; }
function HashIcon()         { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>; }
function BarcodeIcon()      { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="4" width="2" height="16"/><rect x="5" y="4" width="1" height="16"/><rect x="8" y="4" width="2" height="16"/><rect x="12" y="4" width="1" height="16"/><rect x="15" y="4" width="2" height="16"/><rect x="19" y="4" width="1" height="16"/><rect x="21" y="4" width="2" height="16"/></svg>; }
function PrintIcon()        { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>; }
